/**
 * The kiosk's two merchant-setup API calls, in one place:
 *
 *   `fetchKioskConfigurationFunc`  GET `kioskconfiguration/:kioskConfigurationId/fetch` — brand, services, media
 *   `fetchKioskDeviceFunc`         GET `kioskdevice/fetch`                              — this panel's payment options
 *
 * Both are watched actions: `fetchKioskConfig(id)` — dispatched by `useKioskConfigFetch` (from
 * `KioskApp`, so when the kiosk mounts and again if the id changes), for the
 * `kioskConfigurationId` the branch record stored — and `fetchKioskDevice()`, dispatched by the
 * payment screen. One file for both platforms — the call and the mapping are identical, and
 * `apiService` / `endpoints` already resolve per platform.
 *
 * The backend (rista-be `sales-services`, `kiosk_configuration_controller` /
 * `kiosk_device_controller`) serves both under the `Kiosk` licence module. `apiService`'s
 * interceptor hands back the BODY: the configuration document itself (`_id` renamed `mid`), and
 * for the device either its pairing record or `null`.
 *
 * Failure keeps the last-known-good config and device (the `…Failed` reducers clear neither): the
 * slice is persisted precisely so a kiosk on a LAN with no internet keeps running on what it had.
 * The two exceptions are ANSWERS rather than failures, and each replaces what was stored:
 *  - `REST_INVALID_KIOSK_CONFIGURATION` — the id names no configuration of this business any
 *    more (deleted in the merchant panel), so the stored id and config are dropped.
 *  - a `null` device — the panel is not paired, so it has no payment tiles.
 */
import { put, takeLatest } from "redux-saga/effects";

import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  fetchKioskConfig,
  fetchKioskConfigFailed,
  fetchKioskConfigSuccess,
  fetchKioskDevice,
  fetchKioskDeviceFailed,
  fetchKioskDeviceSuccess,
  setKioskConfigurationId,
} from "../../slices/kioskConfigSlice";
import { isKioskConfigurationResponse, toKioskConfig } from "../../../kiosk/config/apiModel";
import { isKioskDevice } from "../../../kiosk/payment/pairing";

/** The backend's answer for an id this business does not own — deleted, or never existed. */
const CONFIGURATION_NOT_FOUND = "REST_INVALID_KIOSK_CONFIGURATION";

/**
 * Is this a device pairing we can use? The one field that matters is `payment` being an array —
 * everything downstream iterates it. Anything else (an error body that came back as a 200, a
 * wrapper) is treated as a failed fetch, so it cannot overwrite a working pairing.
 */
/** The error code on a rejected call (`apiService` rejects with the axios response) or a 200 body. */
function errorCodeOf(value: any): string | undefined {
  return value?.data?.code ?? value?.code;
}

export function* fetchKioskConfigurationFunc(
  action: ReturnType<typeof fetchKioskConfig>,
): Generator<any, void, any> {
  const kioskConfigurationId = action.payload;
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.KIOSK.getKioskConfiguration(kioskConfigurationId),
      skipGlobalLoader: true,
      hideLoader: true,
    } as any);

    if (!isKioskConfigurationResponse(response)) {
      if (errorCodeOf(response) === CONFIGURATION_NOT_FOUND) {
        yield put(setKioskConfigurationId(null));
      }
      yield put(fetchKioskConfigFailed("Kiosk configuration was not in the expected shape"));
      return;
    }

    yield put(fetchKioskConfigSuccess({ config: toKioskConfig(response), now: Date.now() }));
  } catch (error: any) {
    if (errorCodeOf(error) === CONFIGURATION_NOT_FOUND) {
      yield put(setKioskConfigurationId(null));
    }
    yield put(fetchKioskConfigFailed(errorCodeOf(error) ?? "Could not load kiosk configuration"));
  }
}

/**
 * THIS PANEL's payment configuration. The backend finds the pairing by the `deviceIdentifier`
 * header `apiService` attaches — the request carries nothing else. Started by the
 * `fetchKioskDevice` action (`kiosk/payment/hooks.useKioskDeviceReady` dispatches it when the
 * CART and PAYMENT screens open — the cart gates "Proceed to payment" on the answer).
 *
 * A failure keeps the last-known-good device: clearing it would take a working card terminal
 * off a panel because one refresh timed out. A `null` answer does clear it — see the header.
 */
export function* fetchKioskDeviceFunc(): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "GET",
      url: ENDPOINT.KIOSK.getKioskDevice(),
      hideLoader: true,
      skipGlobalLoader: true,
    } as any);

    if (response === null || response === undefined || response === "") {
      yield put(fetchKioskDeviceSuccess({ device: null, now: Date.now() }));
      return;
    }
    if (!isKioskDevice(response)) {
      yield put(fetchKioskDeviceFailed("Kiosk device record was not in the expected shape"));
      return;
    }

    yield put(fetchKioskDeviceSuccess({ device: response, now: Date.now() }));
  } catch (error: any) {
    yield put(fetchKioskDeviceFailed(errorCodeOf(error) ?? "Could not load kiosk device config"));
  }
}

export default function* kioskConfigSaga(): Generator<any, void, any> {
  yield takeLatest(fetchKioskConfig.type, fetchKioskConfigurationFunc);
  yield takeLatest(fetchKioskDevice.type, fetchKioskDeviceFunc);
}
